/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *           _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import { Injectable } from '@angular/core';
import Wavesurfer from 'wavesurfer.js';
import MicrophonePlugin from 'wavesurfer.js/dist/plugin/wavesurfer.microphone';

/**
 * Helper method to create the specific colour line gradient for the waveform.
 *
 * @private
 * @returns CanvasGradient
 */
function createLineGradient(): CanvasGradient {
  const ctx = document.createElement('canvas').getContext('2d');
  const linGrad = ctx.createLinearGradient(0, 64, 0, 200);
  linGrad.addColorStop(0.5, 'rgba(255, 255, 255, 1.000)');
  linGrad.addColorStop(0.5, 'rgba(183, 183, 183, 1.000)');
  return linGrad;
}
/**
 * Default waveform options.
 */
const AudioWaveformDefaultOptions = {
  // Default container:
  container: '#bulb-waveform',
  interact: false,
  cursorWidth: 0,
  plugins: [
    // Initialize the microphone plugin:
    MicrophonePlugin.create({
      bufferSize: 4096,
      numberOfInputChannels: 1,
      numberOfOutputChannels: 1,
      constraints: {
        video: false,
        audio: true,
      },
    }),
  ],
  // The color can be either a simple CSS color or a Canvas gradient
  waveColor: createLineGradient(),
  // $aero-blue colour.
  progressColor: 'rgba(#00AEEF, 0.5)',
  // Make waveform look like SoundCloud's player.
  barWidth: 3,
};

@Injectable()
export class AudioVisualService {
  /**
   * Waveform options.
   */
  private defaultWaveFormOptions = AudioWaveformDefaultOptions;

  /**
   * Create an instance of a WaveForm. Used for visual aspects.
   *
   * @param container The name for the container that the wavesurfer instance will be instantiated on.
   */
  createWaveFormInstance(container : string): Wavesurfer {
    if (container) {
      this.defaultWaveFormOptions.container = container;
    }
    return Wavesurfer.create(this.defaultWaveFormOptions);
  }
}
